import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

import LoginPage from './components/Login';
import DashboardLayout from './components/Dashboard';
import KpEntryPage from './pages/kp/KpEntryPage';
import TteVerificationPage from './pages/TteVerificationPage';
import SsoCallback from './components/SsoCallback';
import GuideKP from './components/GuideKP';
import ResetPassword from './components/ResetPassword';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import TteAdminPage from './pages/TteAdminPage';

// SIPAK KP SESSION FIX V1 — hanya frontend, backend tetap memvalidasi JWT.
// Pemeriksaan exp lokal untuk UX; tidak pernah menggantikan verifikasi signature backend.
const tokenLooksCurrent = (token) => {
  if (!token || typeof token !== 'string') return false;
  try {
    const chunks = token.split('.');
    if (chunks.length !== 3 || !chunks[2]) return false;
    const payload = chunks[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')));
    return Number.isFinite(claims.exp) && (claims.exp * 1000) > (Date.now() + 5000);
  } catch (_) { return false; }
};

const SessionMessage = ({title,detail,onRetry}) => (
  <main className="min-h-screen flex items-center justify-center p-5 bg-slate-50">
    <section role="alert" className="max-w-lg w-full bg-white border rounded-2xl p-7 text-center space-y-4">
      <h1 className="font-bold text-2xl text-slate-900">{title}</h1>
      <p className="text-slate-600 text-lg">{detail}</p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <a href="/" className="rounded-xl bg-blue-700 text-white px-5 py-3 font-semibold">Masuk ke SIPAK</a>
        {onRetry && <button onClick={onRetry} className="rounded-xl border border-blue-700 text-blue-700 px-5 py-3 font-semibold">Coba lagi</button>}
      </div>
    </section>
  </main>
);

const ProtectedRoute = ({ children, verifyKp = false }) => {
  const token = localStorage.getItem('token');
  const fresh = tokenLooksCurrent(token);
  const [gate, setGate] = React.useState('checking');
  const [gateDetail, setGateDetail] = React.useState('');
  const [retry, setRetry] = React.useState(0);

  React.useEffect(() => {
    if (token && !fresh) localStorage.removeItem('token');
  }, [token, fresh]);

  React.useEffect(() => {
    if (!verifyKp || !fresh) return undefined;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let active = true;
    setGate('checking');
    setGateDetail('');
    const api = process.env.REACT_APP_API_URL || 'https://api-pak.sudindikjt2.site';
    fetch(`${api}/api/kp/access`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      signal: controller.signal
    }).then(async response => {
      const data = await response.json().catch(() => ({}));
      if (!active) return;
      const msg = String(data.msg || data.message || '');
      if (response.status === 401 || (response.status === 403 && /token tidak valid|token invalid|jwt expired|jwt malformed/i.test(msg))) {
        localStorage.removeItem('token');
        setGate('expired');
      } else if (response.status === 403 || (response.ok && data.allowed === false)) {
        setGateDetail(msg || 'Pengusulan Kenaikan Pangkat belum dibuka untuk akun ini.');
        setGate('denied');
      } else if (response.ok && data.allowed === true) {
        setGate('ready');
      } else {
        setGateDetail(response.status >= 500 ? 'Server sedang mengalami kendala. Coba lagi beberapa saat.' : msg || 'Tidak dapat memeriksa akses KP.');
        setGate('error');
      }
    }).catch(err => {
      if (active) {
        setGateDetail(err.name === 'AbortError' ? 'Pemeriksaan akses terlalu lama. Coba lagi.' : 'Gagal menghubungi server. Periksa koneksi lalu coba lagi.');
        setGate('error');
      }
    }).finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [verifyKp, token, fresh, retry]);

  if (!fresh || gate === 'expired') {
    return <SessionMessage title="Sesi Anda telah berakhir" detail="Silakan masuk kembali menggunakan NIP dan password." />;
  }
  if (!verifyKp) return children;
  if (gate === 'checking') return <main className="min-h-screen flex items-center justify-center p-8 text-slate-600 text-lg" role="status">Memeriksa akses Kenaikan Pangkat...</main>;
  if (gate === 'denied') return <SessionMessage title="Pengusulan belum dapat dibuka" detail={gateDetail} />;
  if (gate === 'error') return <SessionMessage title="Belum bisa membuka pengusulan" detail={gateDetail} onRetry={() => setRetry(v => v + 1)} />;
  return children;
};


const DashboardWithKpShortcut = () => (
  <>
    <DashboardLayout />
  </>
);

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/panduan-kenaikan-pangkat" element={<GuideKP />} />
        <Route path="/lupa-password" element={<ResetPassword />} />
        {/* Login manual SIPAK tetap tersedia */}
        <Route path="/" element={<LoginPage />} />

        {/* Entry point SSO dari SAPE */}
        <Route path="/auth/sso" element={<SsoCallback />} />

        {/* Dashboard SIPAK */}
        
        {/* Modul Usulan Kenaikan Pangkat */}
        <Route
          path="/kenaikan-pangkat"
          element={
            <ProtectedRoute verifyKp>
              <KpEntryPage />
            </ProtectedRoute>
          }
        />

<Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <DashboardWithKpShortcut />
            </ProtectedRoute>
          }
        />

                {/* Verifikasi TTE - backend tetap membatasi Admin & Satlak */}
        <Route
          path="/tte-verifikasi"
          element={
            <ProtectedRoute>
              <TteVerificationPage />
            </ProtectedRoute>
          }
        />

<Route path="*" element={<Navigate to="/" replace />} />
        <Route
  path="/tte"
  element={
    <ProtectedRoute>
      <TteAdminPage />
    </ProtectedRoute>
  }
/>
      </Routes>
      <MaintenanceOverlay />
    </Router>
  );
}

export default App;
